/**
 * Projects — DESIGN.md §6.1, §13.1.
 *
 * "Any member MAY create a project." The list shows open-card counts and the
 * latest health badge (§6.14), because a project list that says only "name" is
 * a directory, not a status board.
 */
import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { FolderKanban, Lock, Plus } from 'lucide-react';
import type { ProjectHealth } from '@slate/shared';
import { Page } from '@/components/page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingRegion } from '@/components/ui/spinner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCreateProject, useProjectsPaginated } from '@/features/projects/api';
import { relativeTime } from '@/lib/utils';

export const Route = createFileRoute('/projects/')({
  component: ProjectsPage,
});

/** §6.14 — the badge on the project list. */
export const HEALTH: Record<
  ProjectHealth,
  { label: string; emoji: string; variant: 'success' | 'warning' | 'destructive' }
> = {
  on_track: { label: 'On track', emoji: '🟢', variant: 'success' },
  at_risk: { label: 'At risk', emoji: '🟡', variant: 'warning' },
  off_track: { label: 'Off track', emoji: '🔴', variant: 'destructive' },
};

function ProjectsPage(): JSX.Element {
  const [showArchived, setShowArchived] = useState(false);
  const query = useProjectsPaginated(showArchived ? 'all' : 'active');
  const isLoading = query.isLoading;
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];
  const total = query.data?.pages[0]?.totalCount ?? 0;
  const [creating, setCreating] = useState(false);

  return (
    <Page
      title="Projects"
      description="Containers for cards. A card can belong to several at once."
      actions={
        <>
          <Button variant="ghost" size="sm" onClick={() => setShowArchived((v) => !v)}>
            {showArchived ? 'Hide archived' : 'Show archived'}
          </Button>
          <Button onClick={() => setCreating(true)}>
            <Plus />
            New project
          </Button>
        </>
      }
    >
      {isLoading ? (
        <LoadingRegion label="Loading projects" />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<FolderKanban className="size-8" />}
          title="No projects yet"
          description="A project groups cards into sections like “Sprint 34” or “Icebox”. Any member can create one."
          action={<Button onClick={() => setCreating(true)}>Create the first project</Button>}
        />
      ) : (
        <>
          <p className="text-muted-foreground mb-2 text-xs">
            {total} {total === 1 ? 'project' : 'projects'}
          </p>
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((project) => (
              <li key={project.id}>
                <Link
                  to="/projects/$key"
                  params={{ key: project.key }}
                  className="hover:border-primary/50 block h-full rounded-lg border p-4 transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <span
                      className="mt-1 size-3 shrink-0 rounded"
                      style={{ backgroundColor: project.color }}
                      aria-hidden="true"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-2 font-medium">
                        <span className="truncate">{project.name}</span>
                        <span className="text-muted-foreground shrink-0 font-mono text-xs">
                          {project.key}
                        </span>
                      </p>
                      {project.description ? (
                        <p className="text-muted-foreground mt-1 line-clamp-2 text-sm">
                          {project.description}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className="text-muted-foreground text-xs">
                      {project.openTaskCount} open
                    </span>
                    {project.status === 'archived' ? (
                      <Badge variant="secondary">Archived</Badge>
                    ) : null}
                    {/* #9 — visible here so the people who *can* see it know
                        the people who cannot. */}
                    {project.isPrivate ? (
                      <Badge variant="secondary">
                        <Lock className="size-3" /> Private
                      </Badge>
                    ) : null}
                    {project.latestStatus ? (
                      <Badge variant={HEALTH[project.latestStatus.health].variant}>
                        {HEALTH[project.latestStatus.health].emoji}{' '}
                        {HEALTH[project.latestStatus.health].label}
                        {project.latestStatus.isStale ? ' · stale' : ''}
                      </Badge>
                    ) : null}
                    {project.latestStatus ? (
                      <span className="text-muted-foreground text-xs">
                        {relativeTime(project.latestStatus.createdAt)}
                      </span>
                    ) : null}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
          {query.hasNextPage ? (
            <div className="mt-4 flex justify-center">
              <Button
                variant="secondary"
                onClick={() => void query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
              >
                {query.isFetchingNextPage ? 'Loading…' : `Load more (${total - items.length} left)`}
              </Button>
            </div>
          ) : null}
        </>
      )}

      <NewProjectDialog open={creating} onOpenChange={setCreating} />
    </Page>
  );
}

function NewProjectDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const create = useCreateProject();
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [description, setDescription] = useState('');
  const [keyTouched, setKeyTouched] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);

  const reset = (): void => {
    setName('');
    setKey('');
    setDescription('');
    setKeyTouched(false);
    setIsPrivate(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New project</DialogTitle>
          <DialogDescription>
            The key appears in URLs — <code>/projects/TLC</code> — and is how the team refers to the
            project.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="project-name">Name</Label>
            <Input
              id="project-name"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                // Suggest a key from the name until the user edits it themselves.
                if (!keyTouched) setKey(suggestKey(event.target.value));
              }}
              placeholder="Touchline Connect"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="project-key">Key</Label>
            <Input
              id="project-key"
              value={key}
              onChange={(event) => {
                setKeyTouched(true);
                setKey(event.target.value.toUpperCase());
              }}
              placeholder="TLC"
              maxLength={10}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="project-description">Description</Label>
            <Input
              id="project-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="What this project is for"
            />
          </div>
          {/* #9 — asked at creation, because a project that is public for its
              first ten minutes was never private. */}
          <label className="flex items-start gap-2.5 rounded-md border p-2.5 text-sm">
            <Checkbox
              checked={isPrivate}
              onCheckedChange={(next) => setIsPrivate(next === true)}
              className="mt-0.5"
              aria-describedby="project-private-hint"
            />
            <span>
              <span className="font-medium">Private project</span>
              <span id="project-private-hint" className="text-muted-foreground block text-xs">
                Only you, the people you add, and administrators can see it — in the project list,
                in search, and in every card view. You can change this later.
              </span>
            </span>
          </label>
          <p className="text-muted-foreground text-xs">
            Two sections — “In progress” and “Backlog” — are created so the board is not empty.
          </p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={name.trim() === '' || key.trim().length < 2 || create.isPending}
            onClick={() =>
              create.mutate(
                {
                  name: name.trim(),
                  key: key.trim(),
                  description: description.trim() || null,
                  color: '#6366f1',
                  icon: null,
                  sectionNames: ['In progress', 'Backlog'],
                  isPrivate,
                },
                {
                  onSuccess: () => {
                    reset();
                    onOpenChange(false);
                  },
                },
              )
            }
          >
            Create project
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** "Touchline Connect" → "TC"; "Slate" → "SLATE". */
function suggestKey(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) return words[0]!.slice(0, 5).toUpperCase();
  return words
    .map((word) => word[0])
    .join('')
    .slice(0, 5)
    .toUpperCase();
}
